import {
  type FC,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import {
  Box,
  Button,
  ButtonGroup,
  type ButtonProps,
  CheckIcon,
  CloseIcon,
  ConfirmationDialog,
  IconButton,
  type IconButtonProps,
  MotionBox,
  motionDurations,
  motionEasings,
  RedoIcon,
  Row,
  Tooltip,
  UndoIcon,
  useDisclosure,
  useToken,
} from "@hightouch/htui";
import { MotionConfig, type Transition, type Variants } from "motion/react";
import { useFormContext } from "react-hook-form";

import type { V2ResourceToPermission } from "src/components/permission/types-v2";
import type { ResourcePermissionInput } from "src/components/permission/use-resource-permission";
import type { OrganizationPermissionInput } from "src/components/permission/use-organization-permissions";
import { withPermission } from "src/components/permission";
import { EASE_OUT_CUBIC } from "src/utils/easings";

import { useHightouchFormContext } from "./hooks";

export const FORM_BAR_SECTION_HEIGHT = 82;

type FormBarButtonVariant = "submit" | "danger" | "default";

type FormConfirmation = {
  message: ReactNode;
  title: string;
};

type FormBarIconButtonProps = {
  tooltip?: string;
} & IconButtonProps;

type FormBarButtonProps = {
  onClick?: () => void;
  tooltip?: string;
  variant?: FormBarButtonVariant;
  /** Forces the pressed appearance without a real pointer press — used to
   *  mirror the click `:active` state when the button is invoked via keyboard
   *  shortcut, which never triggers `:active`. */
  isPressed?: boolean;
} & Pick<ButtonProps, "children" | "isDisabled" | "isLoading" | "icon">;

const INTERACTIVE_BG = "whiteAlpha.08";

const sharedButtonStyles = {
  boxShadow: "none",
  // Note: important! is required here to override some of the hover/active/disabled styles that Chakra applies
  bg: "transparent",
  _active: { bg: `${INTERACTIVE_BG}!` },
  _disabled: { bg: "transparent!" },
  _loading: { bg: "transparent!" },
};

export const FormBarIconButton = forwardRef<
  HTMLButtonElement,
  FormBarIconButtonProps
>(({ onClick, isDisabled, icon, tooltip, ...props }, ref) => {
  return (
    <Tooltip closeOnScroll={false} message={tooltip} isDisabled={!tooltip}>
      <IconButton
        ref={ref}
        variant="primary"
        onClick={(e) => onClick?.(e)}
        icon={() => <Box as={icon} boxSize={4} color="inherit" />}
        isDisabled={isDisabled}
        color={isDisabled ? "text.tertiary" : "white"}
        _hover={{
          bg: `${INTERACTIVE_BG}!`,
        }}
        {...sharedButtonStyles}
        {...props}
      />
    </Tooltip>
  );
});
FormBarIconButton.displayName = "FormBarIconButton";

export const FormBarButton = forwardRef<HTMLButtonElement, FormBarButtonProps>(
  (
    {
      children,
      onClick,
      isDisabled,
      icon,
      tooltip,
      variant = "default",
      isPressed = false,
      ...props
    },
    ref,
  ) => {
    return (
      <Tooltip closeOnScroll={false} message={tooltip} isDisabled={!tooltip}>
        <Box
          as={Button}
          ref={ref}
          variant="primary"
          onClick={onClick}
          icon={
            icon
              ? () => <Box as={icon} boxSize={4} color="inherit" />
              : undefined
          }
          isDisabled={isDisabled}
          color={
            isDisabled
              ? "text.tertiary"
              : variant === "default"
                ? "white"
                : variant === "submit"
                  ? "green.200"
                  : "danger.300"
          }
          _hover={{
            bg: `${INTERACTIVE_BG}!`,
            color: variant === "danger" ? "danger.200" : "white",
          }}
          {...sharedButtonStyles}
          {...(isPressed && !isDisabled ? { bg: `${INTERACTIVE_BG}!` } : {})}
          {...props}
        >
          {children}
        </Box>
      </Tooltip>
    );
  },
);

FormBarButton.displayName = "FormBarButton";

const PermissionedFormBarButton = withPermission(FormBarButton);

type FormBarWrapperProps = {
  isDisabled?: boolean;
};

const motionVariants: Variants = {
  hidden: {
    y: 16,
    scale: 0.97,
    opacity: 0,
    filter: "blur(6px)",
  },
  visible: {
    y: 0,
    scale: 1,
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.15, ease: EASE_OUT_CUBIC },
  },
};

const layoutTransition: Transition = {
  duration: motionDurations.fast,
  ease: motionEasings.easeInOutQuad,
};

export const FormBarWrapper: FC<PropsWithChildren<FormBarWrapperProps>> = ({
  children,
  isDisabled = false,
}) => {
  // Motion keeps the corners round while the bar resizes only for a pixel radius passed through `style`.
  const borderRadius = useToken("radii", "2xl");

  return (
    <Row
      mx="auto"
      h={`${FORM_BAR_SECTION_HEIGHT}px`}
      width="min-content"
      position="sticky"
      pointerEvents="auto"
      bottom={0}
      flexShrink={0}
      align="center"
      zIndex={3}
      mt="auto"
      visibility={isDisabled ? "hidden" : "visible"}
    >
      <MotionConfig transition={layoutTransition}>
        <MotionBox
          layout
          p={2}
          position="relative"
          overflow="hidden"
          bg="base.containerBackground"
          backdropFilter="blur(4px)"
          style={{ borderRadius }}
          boxShadow="darkPopover"
          bottom={0}
          variants={motionVariants}
          initial={isDisabled ? "hidden" : "visible"}
          animate={isDisabled ? "hidden" : "visible"}
        >
          {/* Animates position only, so the bar's resize does not stretch the buttons and text.
              Positioned so content that AnimatePresence pops out of the layout keeps its place. */}
          <MotionBox layout="position" position="relative">
            <ButtonGroup>{children}</ButtonGroup>
          </MotionBox>
        </MotionBox>
      </MotionConfig>
    </Row>
  );
};

type PermissionedFormBarButtonProps<V2Resource extends V2ResourceToPermission> =
  {
    permission?: ResourcePermissionInput<V2Resource>;
    organizationPermission?: OrganizationPermissionInput;
  } & FormBarButtonProps;

export const FormBarSaveButton = <V2Resource extends V2ResourceToPermission>(
  props: PermissionedFormBarButtonProps<V2Resource>,
) => {
  return (
    <PermissionedFormBarButton icon={CheckIcon} variant="submit" {...props} />
  );
};

export const FormBarDiscardButton: FC<FormBarButtonProps> = (props) => {
  return (
    <PermissionedFormBarButton icon={CloseIcon} variant="danger" {...props} />
  );
};

export const FormBarHistoryButtons: FC<{
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}> = (props) => (
  <ButtonGroup>
    <FormBarIconButton
      aria-label="Undo"
      icon={UndoIcon}
      isDisabled={!props.canUndo}
      onClick={props.onUndo}
    >
      {null}
    </FormBarIconButton>
    <FormBarIconButton
      aria-label="Redo"
      icon={RedoIcon}
      isDisabled={!props.canRedo}
      onClick={props.onRedo}
    >
      {null}
    </FormBarIconButton>
  </ButtonGroup>
);

type FormBarProps<V2Resource extends V2ResourceToPermission> = {
  permission?: ResourcePermissionInput<V2Resource>;
  organizationPermission?: OrganizationPermissionInput;
  confirmation?: FormConfirmation;
  onSubmit?: () => void;
  onDiscard?: () => void;
  isDisabled?: boolean;
  /** Disables only the save button, leaving discard active — for when the form
   *  is dirty but not yet valid, so the user can still back out. */
  isSubmitDisabled?: boolean;
  tooltip?: string;
  saveButtonText?: string;
  discardButtonText?: string;
  alwaysShowDiscard?: boolean;
};

export const FormBar = <V2Resource extends V2ResourceToPermission>({
  permission,
  organizationPermission,
  confirmation,
  onSubmit,
  onDiscard,
  isDisabled = false,
  isSubmitDisabled = false,
  tooltip,
  saveButtonText = "Save changes",
  discardButtonText = "Discard",
  alwaysShowDiscard = false,
}: FormBarProps<V2Resource>) => {
  const {
    formState: { isSubmitting, isDirty },
    reset,
  } = useFormContext();
  const disclosure = useDisclosure();
  const { submit } = useHightouchFormContext();

  const isLoading = isSubmitting;
  const formBarDisabled = !isDirty && !alwaysShowDiscard;
  const submitDisabled =
    isDisabled || isSubmitDisabled || isLoading || !isDirty;
  const discardDisabled = isDisabled || (!alwaysShowDiscard && !isDirty);

  return (
    <FormBarWrapper isDisabled={formBarDisabled}>
      <FormBarSaveButton
        onClick={() => {
          onSubmit?.();
          if (confirmation) {
            disclosure.onOpen();
          } else {
            submit();
          }
        }}
        isDisabled={submitDisabled}
        isLoading={isLoading}
        permission={permission}
        organizationPermission={organizationPermission}
        tooltip={tooltip}
      >
        {saveButtonText}
      </FormBarSaveButton>
      {!isLoading && (
        <FormBarDiscardButton
          onClick={() => {
            reset();
            onDiscard?.();
          }}
          isDisabled={discardDisabled}
        >
          {discardButtonText}
        </FormBarDiscardButton>
      )}
      {confirmation && (
        <ConfirmationDialog
          isOpen={disclosure.isOpen}
          onClose={disclosure.onClose}
          onConfirm={async () => {
            await submit();
            disclosure.onClose();
          }}
          variant="warning"
          confirmButtonText="Confirm"
          title={confirmation.title}
        >
          {confirmation.message}
        </ConfirmationDialog>
      )}
    </FormBarWrapper>
  );
};
